/**
 * LOCATION: dashboard-business/src/pages/Chats.js
 * Basic plan: shows upgrade wall instead of chats
 * FA icons only — zero emojis, zero lucide
 */

import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useChats } from '../hooks/useChats';
import API from '../services/api';
import AuthService from '../services/auth';

const loadFA = () => {
  if (!document.getElementById('fa-cdn-chats')) {
    const fa = document.createElement('link');
    fa.id = 'fa-cdn-chats'; fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
    document.head.appendChild(fa);
  }
};

// ── Upgrade Wall for Basic plan ───────────────────────────────
const UpgradeWall = () => (
  <div style={wall.wrap}>
    <div style={wall.card}>
      {/* Icon */}
      <div style={wall.iconRing}>
        <div style={wall.iconInner}>
          <i className="fa-solid fa-comments" style={{ color: '#667eea', fontSize: 32 }}></i>
        </div>
      </div>

      <h2 style={wall.title}>Live Chats — Pro Feature</h2>
      <p style={wall.sub}>
        The Live Chats feature is not available on the Basic plan.<br />
        Upgrade to <strong>Pro</strong> or <strong>PREMIUM</strong> to handle customer conversations in real time.
      </p>

      {/* Feature list */}
      <div style={wall.featGrid}>
        {[
          { icon: 'fa-headset',    color: '#667eea', title: 'Human Agents',    desc: 'AI escalates to your team when needed' },
          { icon: 'fa-bolt',       color: '#f59e0b', title: 'Real-time Chat',  desc: 'Live conversations as they happen' },
          { icon: 'fa-language',   color: '#10b981', title: 'Multilingual',    desc: 'AI responds in 6+ languages' },
          { icon: 'fa-chart-bar',  color: '#8b5cf6', title: 'Full Analytics',  desc: 'Track chat performance & satisfaction' },
        ].map(f => (
          <div key={f.title} style={wall.featCard}>
            <div style={{ ...wall.featIcon, background: f.color + '15' }}>
              <i className={`fa-solid ${f.icon}`} style={{ color: f.color, fontSize: 18 }}></i>
            </div>
            <div style={wall.featTitle}>{f.title}</div>
            <div style={wall.featDesc}>{f.desc}</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={wall.actions}>
        <a href="/billing" style={wall.ctaPrimary}>
          <i className="fa-solid fa-arrow-up"></i>
          Upgrade to Pro
        </a>
        <a href="/billing" style={wall.ctaSecondary}>
          <i className="fa-solid fa-crown"></i>
          See all plans
        </a>
      </div>

      <p style={wall.note}>
        <i className="fa-solid fa-rotate-left" style={{ marginRight: 5, color: '#9ca3af' }}></i>
        Cancel anytime — no long-term contracts
      </p>
    </div>
  </div>
);

const wall = {
  wrap:        { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 120px)', padding: '20px' },
  card:        { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '48px 40px', maxWidth: 560, width: '100%', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.07)' },
  iconRing:    { width: 96, height: 96, borderRadius: '50%', background: 'rgba(102,126,234,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' },
  iconInner:   { width: 72, height: 72, borderRadius: '50%', background: 'rgba(102,126,234,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title:       { fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 12px' },
  sub:         { fontSize: 15, color: '#6b7280', lineHeight: 1.7, margin: '0 0 28px' },
  featGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 28, textAlign: 'left' },
  featCard:    { padding: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12 },
  featIcon:    { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  featTitle:   { fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 3 },
  featDesc:    { fontSize: 12, color: '#9ca3af', lineHeight: 1.5 },
  actions:     { display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 14 },
  ctaPrimary:  { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '12px 24px', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', borderRadius: 11, textDecoration: 'none', fontWeight: 700, fontSize: 14, boxShadow: '0 4px 12px rgba(102,126,234,0.35)' },
  ctaSecondary:{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '12px 20px', background: '#f3f4f6', color: '#374151', borderRadius: 11, textDecoration: 'none', fontWeight: 700, fontSize: 14 },
  note:        { fontSize: 12, color: '#9ca3af', margin: 0 },
};

// ── Main Chats Page ────────────────────────────────────────────
const Chats = () => {
  const { chats, loading, refresh } = useChats();
  const [selectedSession,  setSelectedSession]  = useState(null);
  const [messages,         setMessages]         = useState([]);
  const [replyText,        setReplyText]        = useState('');
  const [sending,          setSending]          = useState(false);
  const [loadingMessages,  setLoadingMessages]  = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef        = useRef(null);

  const user = AuthService.getCurrentUser();
  const plan = user?.plan || 'basic';
  const isBasic = plan === 'basic' || plan === 'free';

  useEffect(() => { loadFA(); }, []);
  useEffect(() => { return () => stopPoll(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openChat = async (session) => {
    setSelectedSession(session);
    setMessages([]);
    setLoadingMessages(true);
    try {
      const res = await API.getChatMessages(user.id, session.session_id);
      if (res.success) setMessages(res.messages || []);
    } catch (err) { console.error('Failed to load messages:', err); }
    finally { setLoadingMessages(false); }
    startPoll(session.session_id);
  };

  const closeChat = () => {
    setSelectedSession(null); setMessages([]); setReplyText('');
    stopPoll();
  };

  const startPoll = (sessionId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const res = await API.getChatMessages(user.id, sessionId);
        if (res.success) setMessages(res.messages || []);
      } catch {}
    }, 3000);
  };

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedSession) return;
    setSending(true);
    const txt = replyText.trim();
    setReplyText('');
    try {
      const res = await API.sendMessage(selectedSession.session_id, txt);
      if (res.success) {
        setMessages(prev => [...prev, {
          id: Date.now(), sender_type: 'agent',
          content: txt, created_at: new Date().toISOString(),
        }]);
      }
    } catch (err) { console.error('Failed to send reply:', err); }
    finally { setSending(false); }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  const formatSession = (s) => {
    const last = s.messages?.[s.messages.length - 1];
    return {
      ...s,
      customer: `Customer #${s.session_id?.slice(-6) || '??????'}`,
      message:  last?.content || 'No messages yet',
      time:     last
        ? new Date(last.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : new Date(s.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const formatted = chats.map(formatSession);

  // ── Basic plan: show upgrade wall ─────────────────────────
  if (isBasic) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
          .ch-layout{display:flex;min-height:100vh;font-family:'DM Sans',sans-serif;}
          .ch-main{flex:1;background:#f8fafc;}
          @media(max-width:600px){.ch-layout{flex-direction:column;}}
        `}</style>
        <div className="ch-layout">
          <Sidebar />
          <main className="ch-main">
            <UpgradeWall />
          </main>
        </div>
      </>
    );
  }

  // ── Pro / PREMIUM: full chat UI ───────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .ch-layout{display:flex;min-height:100vh;font-family:'DM Sans',sans-serif;}
        .ch-main{flex:1;padding:32px;background:#f8fafc;min-width:0;}
        .ch-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;flex-wrap:wrap;gap:12px;}
        .ch-title{font-size:28px;font-weight:800;color:#111827;margin:0 0 4px;display:flex;align-items:center;gap:10px;}
        .ch-sub{font-size:14px;color:#6b7280;margin:0;}
        .ch-refresh{display:flex;align-items:center;gap:7px;padding:10px 18px;background:#667eea;color:#fff;border:none;border-radius:9px;cursor:pointer;font-weight:600;font-size:13px;font-family:'DM Sans',sans-serif;}
        .ch-content{display:flex;gap:20px;height:calc(100vh - 160px);min-height:0;}
        .ch-list{display:flex;flex-direction:column;gap:10px;overflow-y:auto;flex-shrink:0;transition:width 0.3s ease;}
        .ch-chat-card{background:#fff;padding:16px;border-radius:12px;box-shadow:0 1px 3px rgba(0,0,0,0.07);cursor:pointer;transition:all 0.2s;border:2px solid transparent;}
        .ch-chat-card:hover{border-color:#e5e7eb;box-shadow:0 4px 12px rgba(0,0,0,0.1);}
        .ch-avatar{width:36px;height:36px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;flex-shrink:0;}
        .ch-status-dot{width:8px;height:8px;border-radius:50%;background:#4ade80;display:inline-block;}
        .ch-window{flex:1;background:#fff;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,0.08);border:1px solid #e5e7eb;display:flex;flex-direction:column;min-width:0;overflow:hidden;}
        .ch-win-hdr{display:flex;justify-content:space-between;align-items:center;padding:16px 20px;border-bottom:1px solid #e5e7eb;background:#f9fafb;}
        .ch-win-avatar{width:42px;height:42px;background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px;}
        .ch-messages{flex:1;overflow-y:auto;padding:20px;display:flex;flex-direction:column;gap:12px;background:#f7f9fc;}
        .ch-bubble{max-width:70%;padding:10px 14px;border-radius:12px;font-size:14px;}
        .ch-reply-area{display:flex;gap:10px;padding:16px;border-top:1px solid #e5e7eb;background:#fff;align-items:flex-end;}
        .ch-reply-inp{flex:1;padding:12px 16px;border:2px solid #e5e7eb;border-radius:12px;font-size:14px;outline:none;resize:none;font-family:'DM Sans',sans-serif;line-height:1.5;transition:border-color 0.2s;}
        .ch-reply-inp:focus{border-color:#667eea;}
        .ch-send{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;border:none;border-radius:12px;width:44px;height:44px;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:all 0.2s;}
        .ch-send:disabled{opacity:0.45;cursor:not-allowed;}
        .ch-close-btn{background:transparent;border:none;cursor:pointer;color:#6b7280;padding:6px;border-radius:8px;display:flex;align-items:center;}
        @media(max-width:768px){.ch-main{padding:16px;}.ch-content{flex-direction:column;height:auto;}.ch-list{width:100%!important;}.ch-title{font-size:22px;}}
      `}</style>

      <div className="ch-layout">
        <Sidebar />
        <main className="ch-main">

          <div className="ch-header">
            <div>
              <h1 className="ch-title">
                <i className="fa-solid fa-comments" style={{ color: '#667eea' }}></i>
                Live Chats
              </h1>
              <p className="ch-sub">
                {formatted.length} active conversation{formatted.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button className="ch-refresh" onClick={refresh}>
              <i className="fa-solid fa-rotate-right"></i> Refresh
            </button>
          </div>

          <div className="ch-content">
            {/* Chat List */}
            <div className="ch-list" style={{ width: selectedSession ? 320 : '100%' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 28, display: 'block', marginBottom: 10, color: '#667eea' }}></i>
                  Loading chats...
                </div>
              ) : formatted.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                  <i className="fa-solid fa-comments" style={{ fontSize: 56, opacity: 0.2, display: 'block', marginBottom: 14, color: '#667eea' }}></i>
                  <h3 style={{ fontSize: 18, color: '#111827', margin: '0 0 8px' }}>No Active Chats</h3>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                    Customers will appear here when they start chatting via your widget
                  </p>
                </div>
              ) : formatted.map(chat => (
                <div
                  key={chat.id}
                  className="ch-chat-card"
                  onClick={() => openChat(chat)}
                  style={{ border: selectedSession?.id === chat.id ? '2px solid #667eea' : '2px solid transparent' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="ch-avatar">{chat.customer.charAt(0)}</div>
                      <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>{chat.customer}</h4>
                    </div>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                      background: chat.status === 'waiting' ? '#fef3c7' : '#dcfce7',
                      color:      chat.status === 'waiting' ? '#92400e' : '#166534',
                    }}>
                      {chat.status}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {chat.message}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="fa-regular fa-clock" style={{ fontSize: 11 }}></i> {chat.time}
                    </span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                      {chat.mode === 'human'
                        ? <><i className="fa-solid fa-user" style={{ marginRight: 4, color: '#667eea' }}></i>Human</>
                        : <><i className="fa-solid fa-robot" style={{ marginRight: 4, color: '#10b981' }}></i>AI</>}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Window */}
            {selectedSession && (
              <div className="ch-window">
                {/* Header */}
                <div className="ch-win-hdr">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="ch-win-avatar">
                      {formatSession(selectedSession).customer.charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                        {formatSession(selectedSession).customer}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <span className="ch-status-dot" />
                        {selectedSession.status === 'waiting' ? 'Waiting for agent' : 'Active'}
                      </div>
                    </div>
                  </div>
                  <button className="ch-close-btn" onClick={closeChat}>
                    <i className="fa-solid fa-xmark" style={{ fontSize: 18 }}></i>
                  </button>
                </div>

                {/* Messages */}
                <div className="ch-messages">
                  {loadingMessages ? (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>
                      <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 7 }}></i>Loading messages...
                    </div>
                  ) : messages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px', fontSize: 14 }}>
                      No messages yet
                    </div>
                  ) : messages.map((msg, i) => (
                    <div key={msg.id || i} style={{ display: 'flex', alignItems: 'flex-end', gap: 8, justifyContent: msg.sender_type === 'customer' ? 'flex-start' : 'flex-end' }}>
                      {msg.sender_type === 'customer' && (
                        <div style={{ width: 28, height: 28, background: '#e5e7eb', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="fa-solid fa-user" style={{ fontSize: 12, color: '#9ca3af' }}></i>
                        </div>
                      )}
                      <div className="ch-bubble" style={{
                        background: msg.sender_type === 'customer' ? '#fff'
                          : msg.sender_type === 'agent' ? 'linear-gradient(135deg,#667eea,#764ba2)'
                          : '#f0fdf4',
                        color: msg.sender_type === 'agent' ? '#fff' : '#111827',
                        border: msg.sender_type === 'customer' ? '1px solid #e5e7eb'
                          : msg.sender_type === 'ai' ? '1px solid #86efac' : 'none',
                        borderRadius: msg.sender_type === 'customer' ? '12px 12px 12px 2px' : '12px 12px 2px 12px',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {msg.sender_type === 'customer' ? 'Customer'
                            : msg.sender_type === 'agent' ? 'You'
                            : 'AI Assistant'}
                        </div>
                        <div style={{ lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.content}</div>
                        <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: 'right' }}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      {msg.sender_type !== 'customer' && (
                        <div style={{ width: 28, height: 28, background: msg.sender_type === 'agent' ? '#667eea' : '#86efac', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={`fa-solid ${msg.sender_type === 'agent' ? 'fa-user' : 'fa-robot'}`} style={{ fontSize: 12, color: '#fff' }}></i>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply */}
                <div className="ch-reply-area">
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your reply… (Enter to send)"
                    className="ch-reply-inp"
                    rows={2}
                  />
                  <button
                    className="ch-send"
                    onClick={sendReply}
                    disabled={!replyText.trim() || sending}
                  >
                    <i className="fa-solid fa-paper-plane" style={{ fontSize: 16 }}></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Chats;